import { PreventiveMaintenanceIntervalUnit } from '@evimo/types/enums/preventive-maintenance-interval-unit';
import { PreventiveMaintenanceTriggerConditionType } from '@evimo/types/enums/preventive-maintenance-trigger-condition-type';

const TIME_UNITS = [
  PreventiveMaintenanceIntervalUnit.DAY,
  PreventiveMaintenanceIntervalUnit.WEEK,
  PreventiveMaintenanceIntervalUnit.MONTH,
  PreventiveMaintenanceIntervalUnit.YEAR,
] as const;

const METER_UNITS = [
  PreventiveMaintenanceIntervalUnit.SESSION,
  PreventiveMaintenanceIntervalUnit.KWH,
  PreventiveMaintenanceIntervalUnit.HOUR,
] as const;

/** Max condition rows on a trigger (TIME-only, METER-only, or TIME∥METER). */
export const MAX_TRIGGER_CONDITIONS = 2;

export type TriggerConditionSummary = {
  conditionType: string;
  intervalValue: number;
  intervalUnit: string;
};

export type ConditionTypeOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

export type TriggerConditionFormValue = {
  conditionType?: string;
  intervalValue?: number;
  intervalUnit?: string;
};

/**
 * Default Form.List row for a new trigger condition (antd may briefly leave
 * undefined slots; callers should pass this to `add` / `initialValue`).
 */
export function createEmptyTriggerCondition(): TriggerConditionFormValue {
  return {
    conditionType: undefined,
    intervalValue: undefined,
    intervalUnit: undefined,
  };
}

/**
 * Interval units allowed for the selected condition type (cascading UX).
 */
export function intervalUnitsForConditionType(
  conditionType: string | undefined,
): readonly string[] {
  if (conditionType === PreventiveMaintenanceTriggerConditionType.METER) {
    return METER_UNITS;
  }
  if (conditionType === PreventiveMaintenanceTriggerConditionType.TIME) {
    return TIME_UNITS;
  }
  return [];
}

/**
 * Condition-type options for a row: disable TIME/METER if already used on another row.
 * Tolerates undefined Form.List slots.
 */
export function conditionTypeOptionsForRow(
  allOptions: readonly ConditionTypeOption[],
  conditions: readonly ({ conditionType?: string } | undefined)[] | undefined,
  rowIndex: number,
): ConditionTypeOption[] {
  const usedElsewhere = new Set(
    (conditions ?? [])
      .map((c, i) => (i === rowIndex ? undefined : c?.conditionType))
      .filter((t): t is string => Boolean(t)),
  );

  return allOptions.map((opt) => ({
    ...opt,
    disabled: usedElsewhere.has(opt.value),
  }));
}

/**
 * Whether the user may add another condition row (max 2; need an unused type).
 * Tolerates undefined Form.List slots.
 */
export function canAddTriggerCondition(
  conditions: readonly ({ conditionType?: string } | undefined)[] | undefined,
): boolean {
  const list = conditions ?? [];
  if (list.length >= MAX_TRIGGER_CONDITIONS) {
    return false;
  }
  const used = new Set(list.map((c) => c?.conditionType).filter(Boolean));
  return used.size < MAX_TRIGGER_CONDITIONS;
}

/**
 * Human-readable condition summary for trigger lists / selects.
 */
export function formatTriggerConditionsSummary(
  conditions: readonly (TriggerConditionSummary | undefined)[] | undefined,
): string {
  return (conditions ?? [])
    .filter((c): c is TriggerConditionSummary => Boolean(c?.conditionType))
    .map((c) => `${c.conditionType} ${c.intervalValue} ${c.intervalUnit}`)
    .join(' · ');
}

/**
 * Label for a trigger in selects: basis + conditions (no code/name).
 */
export function formatTriggerSelectLabel(
  basis: string,
  conditions: readonly TriggerConditionSummary[],
): string {
  const summary = formatTriggerConditionsSummary(conditions);
  return summary ? `${basis} · ${summary}` : basis;
}

/**
 * Maps task `partIds` to catalog names for table chips.
 * Falls back to the id when a part is missing from the catalog map.
 */
export type ResolvedPartLabel = {
  id: string;
  name: string;
};

export function resolvePartLabels(
  partIds: readonly string[] | undefined | null,
  partNameById: ReadonlyMap<string, string>,
): ResolvedPartLabel[] {
  if (!partIds?.length) {
    return [];
  }
  return partIds.map((id) => ({
    id,
    name: partNameById.get(id) ?? id,
  }));
}

/** Display names only (tests / compact summaries). */
export function resolvePartNames(
  partIds: readonly string[] | undefined | null,
  partNameById: ReadonlyMap<string, string>,
): string[] {
  return resolvePartLabels(partIds, partNameById).map((part) => part.name);
}

export type TaskEditScope = {
  isShared: boolean;
  organizationId?: string | null;
};

/**
 * Who may mutate a library task (mirrors backend `isEditableByOrganization`).
 * SUPER_ADMIN → shared catalog; org user → own org rows only.
 */
export function canEditLibraryTask(
  task: TaskEditScope,
  actor: { isSuperAdmin: boolean; organizationId: string | null | undefined },
): boolean {
  if (actor.isSuperAdmin) {
    return task.isShared;
  }
  const orgId = actor.organizationId ?? null;
  if (!orgId) return false;
  return task.organizationId === orgId;
}
